import React, { useState } from 'react';
import Button3D from '../ui/Button3D';
import Modal3D from '../ui/Modal3D';
import ActionPillMenu from '../ui/ActionPillMenu';
import { jumpLabel } from './useThreadScroll';

export default function ConversationOverlayActions({
    groupControls = true,
    state = 'alone',
    isHost = false,
    busy = false,
    scrolledUp = false,
    newCount = 0,
    onJumpToLatest,
    onInvite,
    onPassTo,
    onReturnToCompany,
    onLeave,
    onCloseGroup,
    onOpenInvitePanel,
}) {
    const [confirmClose, setConfirmClose] = useState(false);
    const showJump = scrolledUp;
    if (!showJump && !groupControls) return null;

    const menuOptions = [
        { key: 'pass', label: 'Pass to…', onSelect: () => onPassTo?.() },
        { key: 'return', label: 'Return to Company inbox', onSelect: () => onReturnToCompany?.() },
    ];

    return (
        <>
            <div className="pointer-events-none relative z-10 flex min-h-[52px] shrink-0 flex-col items-stretch justify-center gap-2 px-4 py-1" data-conversation-overlay="true" style={groupControls ? undefined : {minHeight:0,padding:0}}>
                <div data-jump-slot="true" className="relative flex w-full justify-center" style={{order:2,minHeight:showJump?52:0}}>
                    {showJump && (
                        <Button3D
                            variant="pill"
                            className="pointer-events-auto ia-thread-jump"
                            onClick={onJumpToLatest}
                            data-overlay-action="jump"
                            data-new-count={newCount || undefined}
                            aria-label={newCount ? `${jumpLabel(newCount).replace('↓ ', '')}. Jump to the latest message` : 'Jump to the latest message'}
                        >
                            <span aria-hidden="true">↓</span>
                        </Button3D>
                    )}
                </div>
                {groupControls && (
                    <div className="ml-auto flex min-w-0 flex-wrap items-end justify-end gap-2" data-group-state={state}>
                        {state === 'group' ? (
                            <>
                                {isHost && (
                                    <Button3D variant="pill" className="pointer-events-auto" onClick={() => setConfirmClose(true)} disabled={busy} data-overlay-action="close-group">
                                        Close group conversation
                                    </Button3D>
                                )}
                                <Button3D variant="pill" className="pointer-events-auto" onClick={onLeave} disabled={busy} data-overlay-action="leave">
                                    Leave conversation
                                </Button3D>
                            </>
                        ) : (
                            <>
                                {state === 'invite_pending' ? (
                                    <Button3D
                                        variant="pill"
                                        className="pointer-events-auto ia-awaiting-pill"
                                        onClick={onOpenInvitePanel}
                                        data-overlay-action="awaiting"
                                        aria-live="polite"
                                    >
                                        Awaiting response…
                                    </Button3D>
                                ) : (
                                    <Button3D variant="pill" className="pointer-events-auto" onClick={onInvite} disabled={busy} data-overlay-action="invite">
                                        Invite a colleague
                                    </Button3D>
                                )}
                                <div className="pointer-events-auto">
                                    <ActionPillMenu
                                        menuLabel="More conversation actions"
                                        options={menuOptions}
                                        placement="top"
                                        align="end"
                                        size="compact"
                                        disabled={busy}
                                        triggerLabel="More conversation actions"
                                        triggerProps={{ 'data-overlay-action': 'more' }}
                                        triggerClassName="ia-btn3d ia-btn3d--pill"
                                    >
                                        ⋯
                                    </ActionPillMenu>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
            {confirmClose && (
                <Modal3D
                    title="Close group conversation?"
                    subtitle="Everyone except you is removed and this goes back to one-on-one."
                    stripe="gold"
                    size="small"
                    role="alertdialog"
                    portal
                    onClose={() => setConfirmClose(false)}
                    testId="close-group-confirm"
                    actions={(
                        <>
                            <Button3D onClick={() => setConfirmClose(false)}>Cancel</Button3D>
                            <Button3D variant="go" onClick={() => { setConfirmClose(false); onCloseGroup?.(); }} data-confirm-close-group="true">Close group</Button3D>
                        </>
                    )}
                >
                    <p className="text-sm text-white">The conversation stays open. The people removed get a short alert, and this is recorded in the conversation&apos;s history.</p>
                </Modal3D>
            )}
        </>
    );
}
